import React, { useState, useEffect, useCallback } from 'react';
import ViewOnlyNotice from './ViewOnlyNotice';
import axios from 'axios';
import { useNotification } from '../context/NotificationContext';
import {
  Typography,
  Box,
  Paper,
  Divider,
  Button,
  CircularProgress,
  TextField, // Still needed for Autocomplete's renderInput
  Grid,
  InputAdornment,
  Autocomplete, // Import Autocomplete
  TablePagination // Import TablePagination
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';

// Import the extracted components
import UserTable from './components/UserTable';
import CreateUserDialog from './components/CreateUserDialog';
import EditUserDialog from './components/EditUserDialog';
import Manage2FADialog from './components/Manage2FADialog';
import ConfirmationDialog from './components/ConfirmationDialog'; // Generic confirmation
import { hasPermission } from '../utils/authUtils'; // Import hasPermission

// Main Users component - now acts as an orchestrator
function Users({ users, onSearch, page, rowsPerPage, totalCount, onPaginationChange, readOnly = false }) { // Updated props for pagination
  const { showNotification } = useNotification();
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
  const [open2FADialog, setOpen2FADialog] = useState(false);
  const [openSaveChangesConfirmDialog, setOpenSaveChangesConfirmDialog] = useState(false);
  const [openRoleConfirmDialog, setOpenRoleConfirmDialog] = useState(false);
  const [openStatusConfirmDialog, setOpenStatusConfirmDialog] = useState(false);
  const [openOnDutyConfirmDialog, setOpenOnDutyConfirmDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [pendingOnDutyChange, setPendingOnDutyChange] = useState(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);

  const [userToEdit, setUserToEdit] = useState(null);
  const [userFor2FA, setUserFor2FA] = useState(null);

  const [loading, setLoading] = useState(false); // Loading for initial data (user profile, dropdown options)
  const [actionLoading, setActionLoading] = useState(false); // Loading for specific actions like toggles/saves
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [availableRolesForCreate, setAvailableRolesForCreate] = useState([]);
  const [regions, setRegions] = useState([]);

  // State for dropdown options
  const [vendorOptions, setVendorOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);

  const [pendingChanges, setPendingChanges] = useState({});
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // State for search filters - values will now come from Autocomplete selections
  const [filters, setFilters] = useState({
    vendorFilter: null, // Use null for Autocomplete initial state
    companyFilter: null,
    facilityFilter: null
  });

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Calculate available primary roles based on the logged-in user's role
  const calculateAvailableRoles = (loggedInUser) => {
    if (!loggedInUser) return ['N/A'];
    const userRole = loggedInUser.primaryRole || loggedInUser.role;
    switch(userRole) {
      case 'OW':
      case 'OWp': return ['sOW', 'RM', 'N/A'];
      case 'sOW': return ['RM', 'N/A'];
      case 'RM':
      case 'SP':
      default: return ['N/A'];
    }
  };

  // Fetch initial data (user profile, regions, dropdown options)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
           setError({ message: 'Authentication token not found.', severity: 'error' });
           setLoading(false);
           return;
        };
        const headers = getAuthHeader();

        // --- Fetch Current User Profile ---
        let fetchedUser = null;
        try {
          const profileResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile`, { headers });
          fetchedUser = profileResponse.data;
        } catch (error) {
          if (error.response && error.response.status === 404) {
            try {
              const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile`, { headers });
              fetchedUser = fallbackResponse.data;
            } catch (fallbackError) {
              console.error('Error fetching user from fallback path:', fallbackError);
              const errorMessage = fallbackError.response?.data?.message || fallbackError.response?.data?.error || fallbackError.message || 'Failed to fetch user profile from fallback path.';
              showNotification(errorMessage, 'error');
            }
          } else { throw error; }
        }
        if (!fetchedUser) {
           const userJson = localStorage.getItem('user');
           if (userJson) { try { fetchedUser = JSON.parse(userJson); } catch (e) { console.error('Error parsing user from localStorage', e); } }
        }

        // --- Set User State and Fetch Conditional Data ---
        if (fetchedUser) {
            setCurrentUser(fetchedUser);
            setAvailableRolesForCreate(calculateAvailableRoles(fetchedUser));
            const userRole = fetchedUser.primaryRole;
            const canFetchDropdownData = ['OW', 'sOW', 'OWp', 'RM'].includes(userRole); // Add N/A admin logic if needed

            // Fetch Regions (Conditional)
            if (['RM', 'OW', 'sOW', 'OWp'].includes(userRole)) {
                try {
                    const regionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/regions`, { headers });
                    setRegions(regionsResponse.data);
                } catch (regionsError) {
                     console.error('Error fetching regions:', regionsError);
                     const errorMessage = regionsError.response?.data?.message || regionsError.response?.data?.error || regionsError.message || 'Failed to fetch regions.';
                     setError(prev => ({ ...prev, regionsError: errorMessage }));
                     showNotification(errorMessage, 'error');
                }
            } else { setRegions([]); }

            // Fetch Dropdown Options (Conditional)
            if (canFetchDropdownData) {
                 try {
                     const [vendorRes, companyRes, facilityRes] = await Promise.all([
                         axios.get(`${process.env.REACT_APP_API_URL}/api/users/vendor-list`, { headers }),
                         axios.get(`${process.env.REACT_APP_API_URL}/api/users/distinct/companies`, { headers }),
                         axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/distinct/names`, { headers })
                     ]);
                     // Add an "All" option represented by null or an empty object/string
                    setVendorOptions([{ vendorIdentifier: null, companyName: 'All Vendors' }, ...vendorRes.data]);
                    setCompanyOptions(['All Companies', ...companyRes.data]);
                    setFacilityOptions(['All Facilities', ...facilityRes.data]);
                } catch (dropdownError) {
                    console.error('Error fetching dropdown options:', dropdownError);
                    const errorMessage = dropdownError.response?.data?.message || dropdownError.response?.data?.error || dropdownError.message || 'Failed to load filter options. Please check console or try again later.';
                    // Set a more specific error message and avoid setting default options on failure
                    setError(prev => ({
                        ...prev,
                        message: errorMessage,
                        severity: 'error',
                        dropdownError: true // Add a flag if needed elsewhere
                    }));
                    showNotification(errorMessage, 'error');
                    // Keep options empty to indicate failure
                    setVendorOptions([]);
                    setCompanyOptions([]);
                    setFacilityOptions([]);
                }
            } else {
                // User doesn't have permission to fetch, set empty options (no "All")
                // Or maybe set "All" if they should still see the dropdowns but disabled/empty?
                // Let's keep them empty for now to avoid confusion.
                setVendorOptions([{ vendorIdentifier: null, companyName: 'All Vendors' }]);
                setCompanyOptions(['All Companies']);
                setFacilityOptions(['All Facilities']);
            }

        } else {
             console.warn('Could not load current user data.');
             const errorMessage = 'Failed to load current user profile.';
             setError(prev => ({ ...prev, userError: errorMessage, severity: 'warning' }));
             showNotification(errorMessage, 'warning');
             setAvailableRolesForCreate(['N/A']);
             setRegions([]);
             // Ensure options are empty if user data fails
             setVendorOptions([]);
             setCompanyOptions([]);
             setFacilityOptions([]);
        }

      } catch (err) {
        console.error('Error fetching initial data:', err);
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load necessary data.';
        setError({ message: errorMessage, severity: 'error' });
        showNotification(errorMessage, 'error');
        setAvailableRolesForCreate(['N/A']);
        setRegions([]);
        // Ensure options are empty on general failure
        setVendorOptions([]);
        setCompanyOptions([]);
        setFacilityOptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeader]);

  // Effect to check if there are pending changes
  useEffect(() => {
    setHasPendingChanges(Object.keys(pendingChanges).length > 0);
  }, [pendingChanges]);

  // --- Pending Changes Logic ---
  const addPendingChange = (userId, changeType, data) => {
    if (!userId) return;
    setPendingChanges(prev => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [changeType]: data }
    }));
  };

  const savePendingChanges = async () => {
    setActionLoading(true);
    setError(null);
    let success = true;
    const userIdsToUpdate = Object.keys(pendingChanges);

    for (const userId of userIdsToUpdate) {
      const changes = pendingChanges[userId];
      const effectiveUserId = users.find(u => u.id === userId || u._id === userId)?._id || userId;

      try {
        let updatePayload = {};
        if (changes.secondaryRoles) updatePayload.secondaryRoles = changes.secondaryRoles;
        if (changes.isActive !== undefined) updatePayload.isActive = changes.isActive;

        if (Object.keys(updatePayload).length > 0) {
           if (updatePayload.secondaryRoles) {
               await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}`, updatePayload, { headers: getAuthHeader() });
           } else if (updatePayload.isActive !== undefined) {
               await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}/toggle-active`, {}, { headers: getAuthHeader() });
           }
        }
      } catch (err) {
        console.error(`Error saving changes for user ${userId}:`, err);
        success = false;
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || `Failed to save changes for user ${userId}.`;
        setError({ message: errorMessage, severity: 'error' });
        showNotification(errorMessage, 'error');
        break;
      }
    }

    setActionLoading(false);
    if (success) {
      setPendingChanges({});
      setOpenSaveChangesConfirmDialog(false);
      const successMessage = 'Changes saved successfully';
      setError({ message: successMessage, severity: 'success' });
      showNotification(successMessage, 'success');
      setTimeout(() => setError(null), 3000);
    }
  };

   // --- Filter and Search Handlers ---
   // Updated handler for Autocomplete components
   const handleFilterChange = (name, newValue) => {
     let filterValue = null;

     if (name === 'vendorFilter') {
       // For vendor, the value is the vendorIdentifier from the selected object
       filterValue = newValue ? newValue.vendorIdentifier : null;
       // Handle "All" selection (where vendorIdentifier is null)
       if (filterValue === null && newValue && newValue.companyName === 'All Vendors') {
           filterValue = null; // Ensure it's truly null for "All"
       }
     } else {
       // For company and facility, the value is the string itself
       filterValue = newValue;
       // Handle "All" selection
       if (newValue === 'All Companies' || newValue === 'All Facilities') {
           filterValue = null;
       }
     }

     const updatedFilters = {
       ...filters,
       [name]: filterValue
     };
     setFilters(updatedFilters);

     // Trigger search/filter in parent component immediately on change
     if (onSearch) {
       // Pass only the actual filter values (non-null) to onSearch if needed
       const activeFilters = Object.entries(updatedFilters).reduce((acc, [key, value]) => {
           if (value !== null) {
               acc[key] = value;
           }
           return acc;
       }, {});
       onSearch(activeFilters);
     } else {
       console.warn("onSearch prop not provided to Users component");
     }
   };

   // No longer needed if filtering happens onChange
   // const handleSearchClick = () => {
   //   if (onSearch) {
   //     onSearch(filters);
   //   } else {
   //     console.warn("onSearch prop not provided to Users component");
   //   }
   // };

  // --- Action Handlers (Remain largely the same) ---

  const handleRoleChange = (userId, role) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    if (!user || !currentUser) return;
    if (currentUser._id === user._id) {
      setError({ message: 'You cannot change your own permissions', severity: 'warning' });
      return;
    }
    const newValue = !(user.secondaryRoles?.[role] || false);
    setPendingRoleChange({ userId, role, user, newValue });
    setOpenRoleConfirmDialog(true);
  };

  const confirmRoleChange = () => {
    if (!pendingRoleChange) return;
    const { userId, role, user, newValue } = pendingRoleChange;
    const updatedSecondaryRoles = { ...(user.secondaryRoles || {}), [role]: newValue };
    // Parent component will handle refetching/updating the user list
    // setUsers(updatedUsers); // Removed direct state update
    addPendingChange(user._id || userId, 'secondaryRoles', updatedSecondaryRoles);
    setOpenRoleConfirmDialog(false);
    setPendingRoleChange(null);
  };

  const handleToggleActive = (userId) => {
    const user = users.find(u => u.id === userId || u._id === userId);
     if (!user || !currentUser) return;
    if (currentUser._id === user._id && user.isActive !== false) {
      setError({ message: 'You cannot deactivate your own account', severity: 'warning' });
      return;
    }
    setPendingStatusChange({ userId, user, newValue: !(user.isActive !== false) });
    setOpenStatusConfirmDialog(true);
  };

  const confirmStatusChange = () => {
    if (!pendingStatusChange) return;
    const { userId, user, newValue } = pendingStatusChange;
    // Parent component will handle refetching/updating the user list
    // setUsers(updatedUsers); // Removed direct state update
    addPendingChange(user._id || userId, 'isActive', newValue);
    setOpenStatusConfirmDialog(false);
    setPendingStatusChange(null);
  };

  const handleToggleOnDuty = (userId) => {
    const user = users.find(u => u.id === userId || u._id === userId);
    if (!user) return;
    setPendingOnDutyChange({ userId, driver: user, newValue: !user.isOnDuty });
    setOpenOnDutyConfirmDialog(true);
  };

  const confirmOnDutyChange = async () => {
    if (!pendingOnDutyChange) return;
    const { userId, driver, newValue } = pendingOnDutyChange;
    const effectiveUserId = driver._id || userId;
    setActionLoading(true);
    setError(null);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}/toggle-on-duty`, {}, { headers: getAuthHeader() });
      // Parent component will handle refetching/updating the user list
      // setUsers(updatedUsers); // Removed direct state update
      const successMessage = 'Driver status updated successfully';
      setError({ message: successMessage, severity: 'success' });
      showNotification(successMessage, 'success');
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error toggling on-duty status:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update driver status.';
      setError({ message: errorMessage, severity: 'error' });
      showNotification(errorMessage, 'error');
    } finally {
      setActionLoading(false);
      setOpenOnDutyConfirmDialog(false);
      setPendingOnDutyChange(null);
    }
  };

  const handleEditUser = (user) => {
    console.log('👥 Users.js: handleEditUser called with:', {
      userId: user.id || user._id,
      userName: `${user.firstName} ${user.lastName}`,
      hasNotificationPreferences: !!user.notificationPreferences,
      notificationPreferences: user.notificationPreferences
    });
    
    setUserToEdit(user);
    setOpenEditUserDialog(true);
  };

  const handleManage2FA = (user) => {
    setUserFor2FA(user);
    setOpen2FADialog(true);
  };

  const handleDeleteUser = (userId) => {
     const user = users.find(u => u.id === userId || u._id === userId);
     if (!user || !currentUser) return;
     if (currentUser._id === user._id) {
       setError({ message: 'You cannot deactivate your own account', severity: 'warning' });
       return;
     }
     setPendingDeleteUser(user);
     setOpenDeleteConfirmDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;
    const userId = pendingDeleteUser._id || pendingDeleteUser.id;
    setActionLoading(true);
    setError(null);
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, { headers: getAuthHeader() });
      const isPermanent = response?.data?.permanent;
      // Parent component will handle refetching/updating the user list
      // if (isPermanent) {
      //   setUsers(prev => prev.filter(u => (u._id || u.id) !== userId));
      // } else {
      //   setUsers(prev => prev.map(u => (u._id || u.id) === userId ? { ...u, isActive: false } : u));
      // }
      const successMessage = isPermanent ? 'User permanently deleted' : 'User deactivated successfully';
      setError({ message: successMessage, severity: 'success' });
      showNotification(successMessage, 'success');
      setTimeout(() => setError(null), 3000);
      // TODO: Trigger refetch in parent component
    } catch (err) {
      console.error('Error deleting/deactivating user:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to process user removal.';
      setError({ message: errorMessage, severity: 'error' });
      showNotification(errorMessage, 'error');
    } finally {
      setActionLoading(false);
      setOpenDeleteConfirmDialog(false);
      setPendingDeleteUser(null);
    }
  };

  
    const handleUserCreated = (newUser) => {
       // Parent component will handle refetching/updating the user list
       // setUsers(prevUsers => [...prevUsers, newUser]); // Removed direct state update
       const successMessage = 'User created successfully';
       setError({ message: successMessage, severity: 'success' });
       showNotification(successMessage, 'success');
       setTimeout(() => setError(null), 3000);
       // TODO: Trigger refetch in parent component
       // TODO: Re-fetch dropdown options if a new user might add a new vendor/company/facility?
       // Or update options state locally if possible.
    };
  
    const handleUserUpdated = (updatedUser) => {
       console.log('👥 Users.js: handleUserUpdated called with:', {
         userId: updatedUser.id || updatedUser._id,
         userName: `${updatedUser.firstName} ${updatedUser.lastName}`,
         hasNotificationPreferences: !!updatedUser.notificationPreferences,
         notificationPreferences: updatedUser.notificationPreferences
       });
       
       // Update the userToEdit state so when dialog reopens it has fresh data
       if (userToEdit && (userToEdit.id === updatedUser.id || userToEdit._id === updatedUser._id)) {
         console.log('👥 Users.js: Updating userToEdit with fresh data');
         setUserToEdit(updatedUser);
       }
       
       // Parent component will handle refetching/updating the user list
       // setUsers(prevUsers => prevUsers.map(u => (u._id || u.id) === (updatedUser._id || updatedUser.id) ? updatedUser : u)); // Removed direct state update
       // TODO: Trigger refetch in parent component
       // TODO: Re-fetch dropdown options if user update changes vendor/company?
    };
  // --- Render Logic ---
  return (
    <Box mt={2}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Typography variant="h6">Users</Typography>
        <Divider sx={{ my: 2 }} />
        {readOnly && <ViewOnlyNotice />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            {/* Use hasPermission for Create User button */}
            {!readOnly && hasPermission('canCreateUsers') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateUserDialog(true)}
                sx={{ mr: 2 }}
                disabled={loading || actionLoading}
              >
                Create User
              </Button>
            )}
            {!readOnly && hasPendingChanges && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setOpenSaveChangesConfirmDialog(true)}
                disabled={actionLoading || loading}
              >
                 {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
              </Button>
            )}
          </Box>
          {error && error.message && (
            <Typography color={error.severity === 'success' ? 'success.main' : 'error.main'} variant="body2">
              {error.message}
            </Typography>
          )}
        </Box>

         {/* Search/Filter Section - Conditional Rendering */}
         {/* Use hasPermission for Filter section */}
         {hasPermission('canFilterUsers') && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>Filter Users</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Vendor Filter */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={vendorOptions}
                  getOptionLabel={(option) => option.vendorIdentifier ? `${option.vendorIdentifier} (${option.companyName || 'N/A'})` : (option.companyName === 'All Vendors' ? 'All Vendors' : '')} // Handle "All" and potentially empty initial state
                  value={vendorOptions.find(opt => opt.vendorIdentifier === filters.vendorFilter) || null} // Value should be the selected object or null
                  onChange={(event, newValue) => handleFilterChange('vendorFilter', newValue)}
                  isOptionEqualToValue={(option, value) => option.vendorIdentifier === value?.vendorIdentifier}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendor ID/Number"
                      variant="outlined"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  fullWidth
                  disabled={loading} // Disable while loading options
                />
              </Grid>
              {/* Company Filter */}
              <Grid item xs={12} sm={4}>
                 <Autocomplete
                   options={companyOptions}
                   getOptionLabel={(option) => option || ''}
                   value={filters.companyFilter || null} // Value should be the selected string or null
                   onChange={(event, newValue) => handleFilterChange('companyFilter', newValue)}
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="Company Name"
                       variant="outlined"
                       size="small"
                       InputProps={{
                         ...params.InputProps,
                         startAdornment: (
                           <>
                             <InputAdornment position="start"><SearchIcon /></InputAdornment>
                             {params.InputProps.startAdornment}
                           </>
                         ),
                       }}
                     />
                   )}
                   fullWidth
                   disabled={loading}
                 />
              </Grid>
              {/* Facility Filter */}
              <Grid item xs={12} sm={4}>
                 <Autocomplete
                   options={facilityOptions}
                   getOptionLabel={(option) => option || ''}
                   value={filters.facilityFilter || null} // Value should be the selected string or null
                   onChange={(event, newValue) => handleFilterChange('facilityFilter', newValue)}
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="Facility Name"
                       variant="outlined"
                       size="small"
                       InputProps={{
                         ...params.InputProps,
                         startAdornment: (
                           <>
                             <InputAdornment position="start"><SearchIcon /></InputAdornment>
                             {params.InputProps.startAdornment}
                           </>
                         ),
                       }}
                     />
                   )}
                   fullWidth
                   disabled={loading}
                 />
              </Grid>
              {/* Search Button - Commented out as filtering is now onChange */}
              {/* <Grid item xs={12} sm={12} sx={{ textAlign: 'right' }}>
                 <Button
                   variant="contained"
                   startIcon={<SearchIcon />}
                   onClick={handleSearchClick} // No longer needed
                   disabled={actionLoading || loading}
                 >
                   Search
                 </Button>
              </Grid> */}
            </Grid>
          </Box>
        )}


        {/* User Table Display */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <UserTable
            users={users}
            currentUser={currentUser}
            onEdit={handleEditUser}
            onManage2FA={handleManage2FA}
            onDelete={handleDeleteUser}
            onToggleRole={handleRoleChange}
            onToggleActive={handleToggleActive}
            onToggleOnDuty={handleToggleOnDuty}
          />
        )}
        {/* Pagination Component */}
        <TablePagination
          component="div"
          count={totalCount || 0} // Use totalCount from props
          page={page} // Use page from props
          rowsPerPage={rowsPerPage} // Use rowsPerPage from props
          onPageChange={(event, newPage) => onPaginationChange(newPage, rowsPerPage)}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            onPaginationChange(0, newRowsPerPage); // Reset to page 0 on rows per page change
          }}
          rowsPerPageOptions={[5, 10, 25, 50]} // Standard options
        />
      </Paper>

      {/* Render Dialogs */}
      <CreateUserDialog
        open={openCreateUserDialog}
        onClose={() => setOpenCreateUserDialog(false)}
        currentUser={currentUser}
        availableRoles={availableRolesForCreate}
        regions={regions}
        onUserCreated={handleUserCreated}
      />

      <EditUserDialog
        open={openEditUserDialog}
        onClose={() => setOpenEditUserDialog(false)}
        userToEdit={userToEdit}
        currentUser={currentUser}
        availableRoles={availableRolesForCreate}
        regions={regions}
        onUserUpdated={handleUserUpdated}
      />

      <Manage2FADialog
        open={open2FADialog}
        onClose={() => setOpen2FADialog(false)}
        user={userFor2FA}
        onUserUpdated={handleUserUpdated}
      />

      {/* Confirmation Dialogs */}
       <ConfirmationDialog
         open={openSaveChangesConfirmDialog}
         onClose={() => setOpenSaveChangesConfirmDialog(false)}
         onConfirm={savePendingChanges}
         title="Confirm Save Changes"
         contentText="Are you sure you want to save all pending role and status changes?"
         loading={actionLoading}
       />

      <ConfirmationDialog
        open={openRoleConfirmDialog}
        onClose={() => setOpenRoleConfirmDialog(false)}
        onConfirm={confirmRoleChange}
        title="Confirm Role Change"
        contentText={pendingRoleChange ? `Are you sure you want to ${pendingRoleChange.newValue ? 'add' : 'remove'} the ${pendingRoleChange.role} role for ${pendingRoleChange.user.firstName} ${pendingRoleChange.user.lastName}?` : ''}
      />

       <ConfirmationDialog
         open={openStatusConfirmDialog}
         onClose={() => setOpenStatusConfirmDialog(false)}
         onConfirm={confirmStatusChange}
         title="Confirm Status Change"
         contentText={pendingStatusChange ? (
            <Box>
                <Typography>
                    Are you sure you want to {pendingStatusChange.newValue ? 'activate' : 'deactivate'} the account for {pendingStatusChange.user.firstName} {pendingStatusChange.user.lastName}?
                </Typography>
                {!pendingStatusChange.newValue && (
                    <Typography color="error" sx={{ mt: 1 }}>Warning: Deactivating prevents login.</Typography>
                )}
            </Box>
         ) : ''}
         confirmColor={pendingStatusChange?.newValue ? 'primary' : 'error'}
       />

       <ConfirmationDialog
         open={openOnDutyConfirmDialog}
         onClose={() => setOpenOnDutyConfirmDialog(false)}
         onConfirm={confirmOnDutyChange}
         title="Confirm On-Duty Status Change"
         contentText={pendingOnDutyChange ? `Are you sure you want to set ${pendingOnDutyChange.driver.firstName} ${pendingOnDutyChange.driver.lastName} as ${pendingOnDutyChange.newValue ? 'On-Duty' : 'Off-Duty'}?` : ''}
         loading={actionLoading}
       />

       <ConfirmationDialog
         open={openDeleteConfirmDialog}
         onClose={() => setOpenDeleteConfirmDialog(false)}
         onConfirm={confirmDeleteUser}
         title={pendingDeleteUser?.isActive === false ? "Confirm Permanent Deletion" : "Confirm User Deactivation"}
         contentText={pendingDeleteUser ? (
             <Box>
                 <Typography>
                     Are you sure you want to {pendingDeleteUser.isActive === false ? <strong>permanently delete</strong> : 'deactivate'} the account for {pendingDeleteUser.firstName} {pendingDeleteUser.lastName}?
                 </Typography>
                 <Typography color="error" sx={{ mt: 1, fontWeight: pendingDeleteUser.isActive === false ? 'bold' : 'normal' }}>
                     {pendingDeleteUser.isActive === false ? "WARNING: This action cannot be undone." : "Warning: This action prevents login."}
                 </Typography>
             </Box>
         ) : ''}
         confirmText={pendingDeleteUser?.isActive === false ? "Permanently Delete" : "Deactivate User"}
         confirmColor="error"
         loading={actionLoading}
       />

    </Box>
  );
}

export default Users;
